// Symbol and stock data models

import type { RFC3339, number_int, number_float, Timestamps, SymbolTicker } from '@/models/base-types';
import type { SymbolID, SymbolHistoryEntryID, StockExchangeID } from '@/models/ids';
import type { StockExchange } from '@/models/stock-exchange';

// Stock value data structure - matches Go StockValue exactly
export interface StockValue {
  price:      number_float | null;  // Kursnotierung in Echtzeit, mit einer Verzögerung von bis zu 20 Minuten
  priceOpen:  number_float | null;  // Kurs bei Börseneröffnung
  high:       number_float | null;  // Höchster Kurs des aktuellen Tages
  low:        number_float | null;  // Niedrigster Kurs des aktuellen Tages
  volume:     number_int   | null;  // Handelsvolumen des aktuellen Tages
  marketCap:  number_int   | null;  // Marktkapitalisierung der Aktie
  tradeTime:  RFC3339      | null;  // Zeit des letzten Handels (Go uses *time.Time)
  dataDelay:  number_int   | null;  // Angabe zur Verzögerung von Echtzeitdaten
  volumeAvg:  number_int   | null;  // Durchschnittliches tägliches Handelsvolumen
  pe:         number_float | null;  // Kurs-Gewinn-Verhältnis
  eps:        number_float | null;  // Gewinn pro Aktie
  high52:     number_float | null;  // Höchster Kurs innerhalb von 52 Wochen
  low52:      number_float | null;  // Niedrigster Kurs innerhalb von 52 Wochen
  change:     number_float | null;  // Kursänderung seit dem Handelsschluss des vergangenen Tages
  beta:       number_float | null;  // Beta-Wert
  changePct:  number_float | null;  // Prozentuale Kursänderung seit dem Handelsschluss des vergangenen Tages
  closeYest:  number_float | null;  // Schlusskurs des vergangenen Tages
  shares:     number_int   | null;  // Anzahl der Aktien
  currency:   string       | null;  // Währung
}

// Where a stock value was fetched from - matches Go SymbolDataSource
export type SymbolDataSource = 'GSHEETS' | 'IB';

export const SYMBOL_DATA_SOURCE_LABELS: Record<SymbolDataSource, string> = {
  GSHEETS: 'Google Sheets',
  IB: 'Interactive Brokers',
};

// Outcome of the last option-contract refresh of a symbol - matches Go OptionRefreshStatus.
// EMPTY and NO_CHAIN are explicitly *not* failures: a market that was closed or an underlying
// without listed options is a normal outcome, not something to alert on.
export type OptionRefreshStatus = 'NEVER'
                                | 'OK'
                                | 'EMPTY'
                                | 'NO_CHAIN'
                                | 'ERROR'
                                | 'UNRESOLVED';

export const OPTION_REFRESH_STATUS_LABELS: Record<OptionRefreshStatus, string> = {
  NEVER: 'Nie geprüft',
  OK: 'Aktuell',
  EMPTY: 'Keine Daten',
  NO_CHAIN: 'Keine Optionen',
  ERROR: 'Fehler',
  UNRESOLVED: 'Nicht auflösbar',
};

// Symbol model - matches Go Symbol exactly
export interface Symbol extends Timestamps {
  id: SymbolID;
  ticker: SymbolTicker;
  name: string | null;
  sector: string | null;
  showInTicker: boolean;
  stockExchangeId: StockExchangeID;
  lastUpdated: RFC3339 | null;
  currentData: StockValue | null;
  currentDataSource: SymbolDataSource | null;
  currentUpdateErrorCount: number_int;
  updateErrorAlerted: boolean;

  // Per-symbol option-refresh state, written by the option sync
  lastOptionRefresh: RFC3339 | null;
  lastOptionRefreshSuccess: RFC3339 | null;
  lastOptionRefreshStatus: OptionRefreshStatus;
  lastOptionRefreshCount: number_int;
  lastOptionRefreshError?: string | null; // ADMIN-only on the backend, omitted for normal users
  optionRefreshErrorCount: number_int;

  exchange: StockExchange | null; // lookup field
}

export type SymbolShort = Omit<Symbol, 'exchange'>; // Symbol without lookups

// Symbol history entry model - matches Go SymbolHistoryEntry exactly
export interface SymbolHistoryEntry extends Timestamps {
  id: SymbolHistoryEntryID;
  symbolId: SymbolID;
  ticker: SymbolTicker;
  data: StockValue;
  source: SymbolDataSource;
  timestamp: RFC3339;
}

// API response types moved to src/modelsAPI/symbol.ts